import { invalidateFormConfigs } from '@/features/config/configApi';
import { adminApi } from '../../api/adminApi';

const LIST_TAG = { type: 'ADMIN_FORM_FIELD_CONFIG', id: 'LIST' };

// The Settings table keeps its own copy of the user's configs; refresh it after a change here
const refreshUserFormConfigs = async (_args, { dispatch, queryFulfilled }) => {
	try {
		await queryFulfilled;
		dispatch(invalidateFormConfigs());
	} catch {
		// Nothing changed
	}
};

/**
 * Admin form field config endpoints. A scope is { scope: 'default' | 'ledger' | 'brokerGroup',
 * scopeId } (scopeId is null for the default).
 * - listFormFieldConfigs({ scope, scopeId, configType }): the configs that scope sees, one per
 *   field, each with its own scope and scopeId.
 * - batchUpdateFormFieldConfigs({ scope, scopeId, changes: [{ id, properties }] }): saves every
 *   change into the scope (a copy of config `id` when the scope has no config for that field
 *   yet), all or nothing.
 * - copyFormFieldConfigs({ docIds, targets: [{ scope, scopeId }] }): copies configs into any
 *   ledgers and broker groups, replacing theirs.
 * - resetFormFieldConfigs({ scope, scopeId, docIds }): deletes those configs of a ledger or
 *   broker group, so their fields follow the next scope in line again.
 */
export const adminFormFieldConfigsApi = adminApi.injectEndpoints({
	endpoints: (builder) => ({
		listFormFieldConfigs: builder.query({
			query: (body) => ({ url: '/form-field-configs/list', method: 'POST', body }),
			transformResponse: (response) => response.items,
			providesTags: [LIST_TAG],
		}),
		batchUpdateFormFieldConfigs: builder.mutation({
			query: (body) => ({ url: '/form-field-configs/batch/update', method: 'POST', body }),
			invalidatesTags: [LIST_TAG],
			onQueryStarted: refreshUserFormConfigs,
		}),
		copyFormFieldConfigs: builder.mutation({
			query: (body) => ({ url: '/form-field-configs/copy', method: 'POST', body }),
			invalidatesTags: [LIST_TAG],
			onQueryStarted: refreshUserFormConfigs,
		}),
		resetFormFieldConfigs: builder.mutation({
			query: (body) => ({ url: '/form-field-configs/reset', method: 'POST', body }),
			invalidatesTags: [LIST_TAG],
			onQueryStarted: refreshUserFormConfigs,
		}),
	}),
});

export const {
	useListFormFieldConfigsQuery,
	useBatchUpdateFormFieldConfigsMutation,
	useCopyFormFieldConfigsMutation,
	useResetFormFieldConfigsMutation,
} = adminFormFieldConfigsApi;
